import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_MY_DEALER,
  ADD_CAR_TO_DEALER,
  GET_UNLISTED_CARS_BY_USER_ID_FOR_DEALER,
} from '../../../../services/dealerService/queries';
import CarsSlideShow from '../../../cars/carsSlideShow';
import { CarStatus, ICar } from '../../../../../__generated__/graphql';
import {
  IAddCarToSellingMutation,
  IAddCarToSellingMutationVariables,
  IGetMyDealerQuery,
  IGetUnlistedCarsByUserIdForDealerQuery,
} from '../../../../services/dealerService/__generated__/queries.generated';
import { toast } from 'react-toastify';
import BaseDialog from '../../../../components/dialog';
import { Button } from '../../../button';
import { useNavigate } from 'react-router-dom';
import { useUpdateCarMutation } from '../../../../services/carService/__generated__/queries.generated';
import { toastActions } from '../../../../../store/toastStore';

const InputContainer = styled.div`
  ${tw`flex justify-center items-center w-full`}
`;

const Input = styled.input`
  ${tw`border border-gray-300 border-solid rounded-md h-[44px] w-[360px] w-full`}
`;

const TabContainer = styled.div`
  ${tw`flex mb-6 border-none border-gray-200 w-full justify-center`}
`;

const Tab = styled.button<{ isActive: boolean }>`
  ${tw`px-4 py-2 font-medium text-sm`}
  ${({ isActive }) =>
    isActive ? tw`text-blue-600 border-none font-bold underline` : tw`text-gray-500 border-none`}
  ${({ isActive }) =>
    !isActive &&
    css`
      &:hover {
        ${tw`text-gray-700`}
      }
    `}
`;

const ContentContainer = styled.div`
  ${tw`mt-4 w-full max-w-full min-w-0 box-border`}
`;

const CarsOnSale = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('listed'); // 'listed' or 'unlisted'
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [currentSelectedCarId, setCurrentSelectedCarId] = useState<string | null>(null);
  const [listedSalePrice, setListedSalePrice] = useState<string>('');
  const {
    data: dealerData,
    loading: dealerLoading,
    error: dealerError,
  } = useQuery<IGetMyDealerQuery>(GET_MY_DEALER);

  const [updateCar, { loading: updateCarLoading }] = useUpdateCarMutation({
    refetchQueries: [GET_UNLISTED_CARS_BY_USER_ID_FOR_DEALER],
    onCompleted: data => {
      toast.success('Car updated');
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const { data, loading, error } = useQuery<IGetUnlistedCarsByUserIdForDealerQuery>(
    GET_UNLISTED_CARS_BY_USER_ID_FOR_DEALER,
    {
      fetchPolicy: 'cache-first',
    },
  );

  const [addCarToSelling, { loading: addCarToSellingLoading }] = useMutation<
    IAddCarToSellingMutation,
    IAddCarToSellingMutationVariables
  >(ADD_CAR_TO_DEALER, {
    refetchQueries: [GET_UNLISTED_CARS_BY_USER_ID_FOR_DEALER],
    onCompleted: data => {
      toast.success('Car added to selling');
      setPriceDialogOpen(false);
      setCurrentSelectedCarId(null);
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const renderListingButtons = (car: ICar) => {
    return [
      {
        text: 'List For Sale',
        onClick: () => {
          if (!dealerData?.myDealer?.id) {
            toast.error('Dealer not found');
            return;
          }
          setCurrentSelectedCarId(car.id);
          setPriceDialogOpen(true);
        },
      },
    ];
  };

  const renderButtons = (car: ICar) => {
    return [
      {
        text: 'Update to Pending',
        onClick: () => {
          toastActions.showToastAlert(
            'Update Selling Status', // Title
            `update selling alert here`, // Message
            'update to pending',
            () => {
              updateCar({
                variables: {
                  id: car.id,
                  updateCarInput: {
                    status: CarStatus.Pending,
                  },
                },
              });
            },
            () => {
              // Cancel button callback - do nothing or handle cancellation
              // Todo: delete the refund invoice just created
              toast.info('add operating hours later');
            },
          );
        },
      },
      {
        text: 'Update to Sold',
        onClick: () => {
          toastActions.showToastAlert(
            'Update Selling Status', // Title
            `update selling alert here`, // Message
            'update to sold',
            () => {
              updateCar({
                variables: {
                  id: car.id,
                  updateCarInput: {
                    status: CarStatus.Sold,
                  },
                },
              });
            },
            () => {
              // Cancel button callback - do nothing or handle cancellation
              // Todo: delete the refund invoice just created
              toast.info('add operating hours later');
            },
          );
        },
      },
      {
        text: 'Delist',
        onClick: () => {},
      },
    ];
  };

  const renderContent = () => {
    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    if (activeTab === 'listed') {
      return (
        <CarsSlideShow
          asDealer
          cars={dealerData?.myDealer?.sellingCars as ICar[]}
          carsLoading={loading}
          carsError={error}
          renderButtons={renderButtons}
        />
      );
    }

    if (
      !data ||
      !data.getUnlistedCarsByUserIdForDealer ||
      data.getUnlistedCarsByUserIdForDealer?.length === 0
    ) {
      return <div>No cars available to list</div>;
    }

    return (
      <CarsSlideShow
        asDealer
        cars={data.getUnlistedCarsByUserIdForDealer as ICar[]}
        carsLoading={loading}
        carsError={error}
        renderButtons={renderListingButtons}
      />
    );
  };

  return (
    <>
      <TabContainer>
        <Tab isActive={activeTab === 'listed'} onClick={() => setActiveTab('listed')}>
          Cars Listed
        </Tab>
        <Tab isActive={activeTab === 'unlisted'} onClick={() => setActiveTab('unlisted')}>
          List Cars For Sale
        </Tab>
      </TabContainer>

      <ContentContainer>{renderContent()}</ContentContainer>

      <Button
        onClick={() => navigate(`/dealership/carlist/${dealerData?.myDealer?.id}`)}
        text="View Car List"
      />

      <BaseDialog
        isOpen={priceDialogOpen && !!currentSelectedCarId}
        onClose={() => {
          setPriceDialogOpen(false);
          setCurrentSelectedCarId(null);
        }}
        title="List For Sale"
        description="please enter the price you want to list this car for sale"
        confirmText="List"
        cancelText="Cancel"
        onConfirm={() => {
          if (!currentSelectedCarId || !dealerData?.myDealer?.id) {
            toast.error('Car or Dealer not found');
            return;
          }
          addCarToSelling({
            variables: {
              input: {
                carId: currentSelectedCarId,
                dealerId: dealerData?.myDealer?.id,
                listedSalePrice: Number(listedSalePrice),
              },
            },
          });
        }}
        renderChildren={() => {
          return (
            <InputContainer>
              <Input
                type="text"
                placeholder="Enter Price"
                pattern="[0-9]*"
                inputMode="numeric"
                value={listedSalePrice}
                onChange={e => setListedSalePrice(e.target.value)}
              />
            </InputContainer>
          );
        }}
      />
    </>
  );
};

export default CarsOnSale;
